import { X } from 'lucide-react';

/** Record-count line: Poppins 500 / text-sm / leading-5 / tracking-normal, muted — label and value alike. */
const RECORD_COUNT_CLASS = 'font-poppins text-muted-foreground text-sm leading-5 font-medium tracking-normal';

interface RecordCountHeaderProps {
    /** Server total for the current filter/search context. */
    total: number;
    /** The committed search term; empty string when not searching. */
    search: string;
    /** Clears the search. Rendered as a dismiss control on the term itself — see below. */
    onClearSearch: () => void;
}

/**
 * The record total shown beneath the Network Overview title.
 *
 * Search is an additional filter over Network Records, so while searching every count in the table
 * describes the matching subset rather than the structural total. The wording says so explicitly —
 * an Area row's POI count legitimately shrinks under a search, and that must not read as a bug.
 *
 * The term carries its own dismiss control because this line is the only part of the search that
 * renders in BOTH layouts: the search input is maximised-only, so restoring to split used to leave
 * the table filtered with the term visible here and no way to clear it without maximising again.
 * Putting the control on the term itself fixes that without spending header width in split, which
 * is already the tightest layout.
 */
export function RecordCountHeader({ total, search, onClearSearch }: RecordCountHeaderProps) {
    // Label and value share one type style, so the value is distinguished by tabular-nums alone
    // rather than by colour.
    const count = <span className="tabular-nums">{total.toLocaleString()}</span>;

    if (search === '') {
        return <p className={RECORD_COUNT_CLASS}>Total Network Records: {count}</p>;
    }

    return (
        <p className={RECORD_COUNT_CLASS}>
            {count} Network {total === 1 ? 'Record' : 'Records'} matching{' '}
            {/* inline-flex, not a block: the chip has to sit in the sentence and wrap with it. */}
            <span className="bg-muted text-foreground ml-0.5 inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 align-middle">
                <span className="min-w-0 truncate">“{search}”</span>
                <button
                    type="button"
                    onClick={onClearSearch}
                    aria-label={`Clear search “${search}”`}
                    className="text-origin-blue hover:bg-background shrink-0 cursor-pointer rounded-sm p-0.5"
                >
                    <X className="size-3" size={12} strokeWidth={1.5} absoluteStrokeWidth />
                </button>
            </span>
        </p>
    );
}
